/**
 * GA4 analytics tracking helpers.
 * Safely calls gtag when available (client-side only).
 *
 * MICROSOFT CLARITY IS MIRRORED FROM HERE, DELIBERATELY.
 * The funnel-critical helpers below also emit the matching Clarity API event
 * (`lib/clarity.ts`). Doing it inside these helpers — rather than at each call
 * site — is what keeps the two stacks describing the same funnel: a Clarity
 * smart event wired to a CSS selector, or emitted from only three of the five
 * places that add to cart, produces numbers that silently disagree with GA4.
 * Clarity gets the event NAME and coarse, banded dimensions only; the itemised
 * payload (titles, exact values, transaction ids) stays with GA4.
 */

import { cartValueBand, clarityEvent, claritySet } from "@/lib/clarity";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean | undefined>
): void {
  if (typeof window === "undefined") return;
  const gtag = window.gtag;
  if (typeof gtag === "function") {
    gtag("event", eventName, params);
  }
}

/* ------------------------------------------------------------------ */
/*  gtag readiness                                                     */
/* ------------------------------------------------------------------ */

/** True when gtag is loaded and callable in this document. */
export function isGtagReady(): boolean {
  return typeof window !== "undefined" && typeof window.gtag === "function";
}

/**
 * Run `cb` once `window.gtag` exists, with a BOUNDED wait.
 *
 * Why this exists: `components/analytics/GoogleAnalytics.tsx` loads gtag with
 * `strategy="lazyOnload"`, so on a slow connection every `track*` call made
 * during the first render silently no-ops (each helper early-returns when
 * `window.gtag` is undefined). For a fire-and-forget CTA event that's an
 * acceptable loss; for a one-shot conversion (`purchase`, `view_item`) it is
 * NOT — the event is gone with no trace. Callers that must not lose their
 * event schedule it through here instead of calling the helper directly.
 *
 * Contract:
 *   - if gtag is ALREADY available, `cb` runs SYNCHRONOUSLY (so a caller can
 *     rely on the side-effect having happened before it returns);
 *   - otherwise it polls until gtag appears or `timeoutMs` elapses;
 *   - `cb` runs AT MOST ONCE, and never after the returned canceller is called
 *     (use it as a React effect cleanup so nothing fires post-unmount);
 *   - returns a canceller; SSR/no-window resolves to a no-op immediately.
 *
 * Polling (rather than a load event) is deliberate: `next/script` gives no
 * reliable "gtag configured" signal, and gtag itself is defined by an inline
 * script that runs after the library, so the only honest readiness test is
 * `typeof window.gtag === "function"`.
 */
export function whenGtagReady(
  cb: () => void,
  opts?: { timeoutMs?: number; intervalMs?: number }
): () => void {
  if (typeof window === "undefined") return () => {};

  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    cb();
  };

  if (isGtagReady()) {
    run();
    return () => {
      done = true;
    };
  }

  const intervalMs = opts?.intervalMs ?? 250;
  const timeoutMs = opts?.timeoutMs ?? 15_000;
  const startedAt = Date.now();

  const timer = window.setInterval(() => {
    if (done) {
      window.clearInterval(timer);
      return;
    }
    if (isGtagReady()) {
      window.clearInterval(timer);
      run();
      return;
    }
    if (Date.now() - startedAt >= timeoutMs) {
      // Give up — gtag is blocked (ad-blocker) or consent was declined. The
      // server-side backstop (lib/ga4-measurement.ts, fired from the Stripe
      // webhook) is what covers a lost `purchase`.
      window.clearInterval(timer);
      done = true;
    }
  }, intervalMs);

  return () => {
    done = true;
    window.clearInterval(timer);
  };
}

/* ------------------------------------------------------------------ */
/*  GA4 e-commerce event helpers                                       */
/* ------------------------------------------------------------------ */

interface EcommerceItem {
  item_id: string;
  item_name: string;
  item_category?: string;
  item_variant?: string;
  item_list_id?: string;
  item_list_name?: string;
  index?: number;
  price?: number;
  quantity?: number;
}

/**
 * PRICE UNITS — read this before adding a helper.
 *
 * EVERY helper in this file takes `pricePence` (integer pence — the shape the
 * DB, the cart and `adjustPricePence` all carry) and converts to pounds here,
 * because GA4's `value`/`price` are decimal major units.
 *
 * ONE convention, deliberately. An earlier draft had the list helpers take
 * pounds while every other helper took pence; two opposite conventions in one
 * module is a silent 100× revenue error in GA4 that no unit test can catch
 * across a module boundary. If you add a helper, take pence and divide here.
 */
function toGaItems(
  items: {
    productId: string;
    title: string;
    category?: string;
    variant?: string;
    pricePence?: number | null;
    quantity?: number;
  }[]
): EcommerceItem[] {
  return items.map((i) => ({
    item_id: i.productId,
    item_name: i.title,
    item_category: i.category,
    item_variant: i.variant,
    price: i.pricePence != null ? i.pricePence / 100 : undefined,
    quantity: i.quantity ?? 1,
  }));
}

/** Fire when a user views a product detail (overlay/drawer). */
export function trackViewItem(product: {
  productId: string;
  title: string;
  category?: string;
  variant?: string;
  pricePence?: number | null;
}): void {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", "view_item", {
      currency: "GBP",
      value: product.pricePence != null ? product.pricePence / 100 : undefined,
      items: toGaItems([product]),
    });
  }
}

/* ------------------------------------------------------------------ */
/*  List / selection events                                            */
/*                                                                     */
/*  These are what make "which occasion or landing page drove a product */
/*  click" answerable: `view_item_list` names the surface a product was  */
/*  shown on, `select_item` records the click from that same surface, so */
/*  GA4 can attribute the click (and downstream purchase) to the list.   */
/*  `listId` should be a stable slug (e.g. "occasion-birthday"), and     */
/*  `listName` the human label shown to the customer.                   */
/* ------------------------------------------------------------------ */

/** A product as rendered inside a list. `pricePence` matches every other helper. */
export interface AnalyticsListItem {
  id: string;
  name: string;
  /** Price in integer PENCE (e.g. 4250), converted to pounds in `toListItems`. */
  pricePence?: number | null;
  /** 0-based position in the list, as rendered. */
  index: number;
}

function toListItems(
  listId: string,
  listName: string,
  items: AnalyticsListItem[]
): EcommerceItem[] {
  return items.map((i) => ({
    item_id: i.id,
    item_name: i.name,
    item_list_id: listId,
    item_list_name: listName,
    index: i.index,
    price: i.pricePence != null ? i.pricePence / 100 : undefined,
  }));
}

/** Fire when a list/grid/carousel of products becomes visible. */
export function trackViewItemList(
  listId: string,
  listName: string,
  items: AnalyticsListItem[]
): void {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("event", "view_item_list", {
    item_list_id: listId,
    item_list_name: listName,
    items: toListItems(listId, listName, items),
  });
}

/** Fire when a product is clicked FROM a list (pairs with view_item_list). */
export function trackSelectItem(
  listId: string,
  listName: string,
  item: AnalyticsListItem
): void {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("event", "select_item", {
    item_list_id: listId,
    item_list_name: listName,
    items: toListItems(listId, listName, [item]),
  });
}

/** Fire when a user adds an item to the cart. */
export function trackAddToCart(item: {
  productId: string;
  title: string;
  category?: string;
  pricePence?: number | null;
  quantity?: number;
}): void {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", "add_to_cart", {
      currency: "GBP",
      value: item.pricePence != null ? (item.pricePence / 100) * (item.quantity ?? 1) : undefined,
      items: toGaItems([item]),
    });
  }
  // Clarity: event only. NOT the item title — a product title is safe, but
  // mirroring the itemised payload would invite the next helper to mirror a
  // customer-supplied one.
  clarityEvent("add_to_cart");
}

/** Fire when a user removes an item from the cart. */
export function trackRemoveFromCart(item: {
  productId: string;
  title: string;
  category?: string;
  pricePence?: number | null;
  quantity?: number;
}): void {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", "remove_from_cart", {
      currency: "GBP",
      value: item.pricePence != null ? (item.pricePence / 100) * (item.quantity ?? 1) : undefined,
      items: toGaItems([item]),
    });
  }
}

/** Fire when a user starts checkout. */
export function trackBeginCheckout(items: {
  productId: string;
  title: string;
  category?: string;
  pricePence?: number | null;
  quantity?: number;
}[], totalPence: number): void {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", "begin_checkout", {
      currency: "GBP",
      value: totalPence / 100,
      items: toGaItems(items),
    });
  }
  // Clarity: the event drives the checkout funnel; the BAND (never the exact
  // total) makes "do big baskets abandon more?" answerable from replays.
  clarityEvent("begin_checkout");
  claritySet("cartValueBand", cartValueBand(totalPence));
}

/**
 * Fire when an order's payment has been captured.
 *
 * This is the GA4-standard `purchase` event. Payment is now taken by card via
 * Stripe at checkout, so this fires on the order page AFTER payment is
 * confirmed (not at order placement) and is tagged `payment_status: "paid"` —
 * GA4 "revenue" therefore reflects captured payments. `order_type` segments
 * the funnel ("standard" purchasable products vs a "bespoke" enquiry-led order
 * — enquiry-only items can't reach checkout, so this is "standard" today).
 */
export function trackPurchase(
  orderId: string,
  items: {
    productId: string;
    title: string;
    category?: string;
    pricePence?: number | null;
    quantity?: number;
  }[],
  totalPence: number,
  opts?: { orderType?: "standard" | "bespoke" }
): void {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", "purchase", {
      transaction_id: orderId,
      currency: "GBP",
      value: totalPence / 100,
      items: toGaItems(items),
      payment_status: "paid",
      order_type: opts?.orderType ?? "standard",
    });
  }
  // Clarity: the conversion event + the outcome tag. `orderId` is NOT sent here
  // — it is attached once, as the session identifier, by `clarityIdentify` in
  // `CheckoutSuccessEffects`, so there is exactly one place that decides what
  // order reference reaches a third party.
  clarityEvent("purchase");
  claritySet("paymentOutcome", "paid");
}

/* ------------------------------------------------------------------ */
/*  Lead-funnel event helpers                                          */
/* ------------------------------------------------------------------ */

/** Fire when an enquiry form is submitted successfully. */
export function trackGenerateLead(method?: string): void {
  trackEvent("generate_lead", { method: method || "enquiry_form" });
  // Clarity: event only. `method` is ours, but the enquiry BODY is not, and the
  // safest boundary is to send no free-text-adjacent value from a lead path.
  clarityEvent("generate_lead");
}

/** Fire when a visitor clicks through to WhatsApp. */
export function trackWhatsappClick(location?: string): void {
  trackEvent("whatsapp_click", location ? { location } : undefined);
}

/** Fire when a visitor clicks through to Instagram. */
export function trackInstagramClick(location?: string): void {
  trackEvent("instagram_click", location ? { location } : undefined);
}

/** Fire when a visitor taps a phone (tel:) link. */
export function trackPhoneClick(location?: string): void {
  trackEvent("phone_click", location ? { location } : undefined);
}

/** Fire when the chat widget is first opened. */
export function trackChatStart(): void {
  trackEvent("chat_start");
}

/** Fire when a user sends a free-text message in the chat widget. */
/**
 * A visitor typed a real message in chat (a warmer intent than a chip tap).
 *
 * `topic` was REMOVED 2026-07-26: the caller was passing the visitor's raw first 60
 * characters, which routinely contain a name, postcode, phone number or bereavement.
 * That is PII in GA4 (prohibited by Google's own terms) and it contradicted the bot's
 * own privacy answer. Only non-identifying dimensions belong in an analytics event —
 * what customers actually ask now lives in the admin's own transcript log, which is
 * first-party, retention-bounded and PII-redacted.
 *
 * The parameter is deliberately typed OUT rather than merely unused, so a future
 * caller can't reintroduce it without a type error.
 */
export function trackChatLead(params?: { message_count?: number; char_length?: number }): void {
  trackEvent("chat_lead", params);
}
